import React, { useState, useEffect, useRef } from 'react';
import { AppData } from '../types';
import { AppIcon } from '../components/AppIcon';
import { StoreBadge } from '../components/StoreBadge';
import { GrowthScoreGauge, getGrowthClassification } from '../components/GrowthScoreGauge';
import { appCatalog, STOREFRONT_COUNTRIES } from '../services/appCatalogService';
import { CATEGORIES_LIST } from '../data/mockData';
import { Sparkles, Compass, TrendingUp, Clock, Globe, Filter, Search, Loader2, CheckCircle2, ShieldCheck } from 'lucide-react';

interface NewAppsViewProps {
  onSelectApp: (appId: string) => void;
  onCompare: (appIds: string[]) => void;
}

export const NewAppsView: React.FC<NewAppsViewProps> = ({ onSelectApp, onCompare }) => {
  const [activeTab, setActiveTab] = useState<'new_releases' | 'recently_discovered' | 'fastest_growing'>('new_releases');
  const [search, setSearch] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [selectedCountry, setSelectedCountry] = useState<string>(appCatalog.getSelectedCountry());
  const [appsList, setAppsList] = useState<AppData[]>([]);
  const [totalCount, setTotalCount] = useState<number>(0);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [hasMore, setHasMore] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [isLoadingMore, setIsLoadingMore] = useState<boolean>(false);
  const [discoveryTelemetry, setDiscoveryTelemetry] = useState<any>(null);

  const debounceTimer = useRef<any>(null);

  useEffect(() => {
    // Load discovery telemetry
    appCatalog.getDiscoveryStats().then(stats => {
      if (stats) setDiscoveryTelemetry(stats);
    });
  }, []);

  const fetchApps = async (page = 1, isReset = false) => {
    if (isReset) {
      setIsLoading(true);
      setCurrentPage(1);
    } else {
      setIsLoadingMore(true);
    }

    try {
      const response = await appCatalog.getNewApps({
        tab: activeTab,
        category: selectedCategory,
        country: selectedCountry,
        q: search,
        page,
        limit: 24
      });

      if (isReset) {
        setAppsList(response.results);
      } else {
        setAppsList(prev => [...prev, ...response.results]);
      }
      setTotalCount(response.total);
      setHasMore(response.hasMore);
      setCurrentPage(page);
    } catch (err) {
      console.error('[NewAppsView] Failed to load apps:', err);
    } finally {
      setIsLoading(false);
      setIsLoadingMore(false);
    }
  };

  useEffect(() => {
    if (debounceTimer.current) clearTimeout(debounceTimer.current);
    debounceTimer.current = setTimeout(() => {
      fetchApps(1, true);
    }, 200);

    return () => {
      if (debounceTimer.current) clearTimeout(debounceTimer.current);
    };
  }, [activeTab, selectedCategory, selectedCountry, search]);

  const formatReleaseDate = (dateStr?: string) => {
    if (!dateStr) return 'Recently';
    const date = new Date(dateStr);
    const now = Date.now();
    const diffDays = Math.floor((now - date.getTime()) / (24 * 3600 * 1000));
    if (diffDays === 0) return 'Released Today';
    if (diffDays === 1) return 'Released Yesterday';
    if (diffDays < 30) return `Released ${diffDays}d ago`;
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  const formatDiscoveredDate = (dateStr?: string) => {
    if (!dateStr) return 'Recently';
    const date = new Date(dateStr);
    const now = Date.now();
    const diffHours = Math.floor((now - date.getTime()) / (3600 * 1000));
    if (diffHours < 1) return 'Indexed Just now';
    if (diffHours < 24) return `Indexed ${diffHours}h ago`;
    const diffDays = Math.floor(diffHours / 24);
    return `Indexed ${diffDays}d ago`;
  };

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-950 text-slate-900 dark:text-slate-100 transition-colors">
      {/* Header Banner */}
      <div className="border-b border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900/60 backdrop-blur-md">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div>
              <div className="flex items-center gap-2 mb-2">
                <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold bg-blue-50 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400 border border-blue-200/60 dark:border-blue-800/40">
                  <Sparkles className="w-3.5 h-3.5 text-blue-600 dark:text-blue-400" />
                  Continuous Auto-Ingestion Engine
                </span>
                <span className="inline-flex items-center gap-1 text-xs text-emerald-600 dark:text-emerald-400 font-medium">
                  <span className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse"></span>
                  Live Apple Discovery
                </span>
              </div>
              <h1 className="text-2xl sm:text-3xl font-bold tracking-tight text-slate-900 dark:text-white">
                New Apps & Releases
              </h1>
              <p className="mt-1 text-sm text-slate-600 dark:text-slate-400 max-w-2xl">
                Legitimate newly released iOS applications automatically discovered and verified from Apple Public Storefronts, Chart RSS feeds, and developer studios.
              </p>
            </div>

            {/* Quick Telemetry Counters */}
            <div className="flex items-center gap-3 bg-slate-100 dark:bg-slate-800/60 p-2.5 rounded-xl border border-slate-200/80 dark:border-slate-700/60 text-xs">
              <div className="px-2 border-r border-slate-200 dark:border-slate-700">
                <div className="text-slate-500 dark:text-slate-400">Total Catalog</div>
                <div className="font-bold text-slate-900 dark:text-white">
                  {discoveryTelemetry?.coverageBreakdown?.totalIndexedApps || '885+'}
                </div>
              </div>
              <div className="px-2 border-r border-slate-200 dark:border-slate-700">
                <div className="text-slate-500 dark:text-slate-400">New Releases (30D)</div>
                <div className="font-bold text-emerald-600 dark:text-emerald-400">
                  {discoveryTelemetry?.dailyCatalogGrowth?.newReleasesDetected30D || 'Live'}
                </div>
              </div>
              <div className="px-2">
                <div className="text-slate-500 dark:text-slate-400">Storefronts</div>
                <div className="font-bold text-blue-600 dark:text-blue-400">13 Global</div>
              </div>
            </div>
          </div>

          {/* Navigation Tabs */}
          <div className="flex border-b border-slate-200 dark:border-slate-800 mt-6 gap-2 sm:gap-4 overflow-x-auto">
            <button
              onClick={() => setActiveTab('new_releases')}
              className={`pb-3 px-3 text-sm font-semibold border-b-2 transition-colors flex items-center gap-2 whitespace-nowrap ${
                activeTab === 'new_releases'
                  ? 'border-blue-600 text-blue-600 dark:border-blue-400 dark:text-blue-400'
                  : 'border-transparent text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-slate-200'
              }`}
            >
              <Sparkles className="w-4 h-4" />
              New Releases
              <span className="text-xs px-1.5 py-0.5 rounded bg-blue-100 dark:bg-blue-900/40 text-blue-700 dark:text-blue-300 font-bold">
                ≤ 30 Days
              </span>
            </button>

            <button
              onClick={() => setActiveTab('recently_discovered')}
              className={`pb-3 px-3 text-sm font-semibold border-b-2 transition-colors flex items-center gap-2 whitespace-nowrap ${
                activeTab === 'recently_discovered'
                  ? 'border-blue-600 text-blue-600 dark:border-blue-400 dark:text-blue-400'
                  : 'border-transparent text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-slate-200'
              }`}
            >
              <Compass className="w-4 h-4" />
              Recently Discovered
              <span className="text-xs px-1.5 py-0.5 rounded bg-slate-200 dark:bg-slate-800 text-slate-700 dark:text-slate-300">
                Recently Indexed
              </span>
            </button>

            <button
              onClick={() => setActiveTab('fastest_growing')}
              className={`pb-3 px-3 text-sm font-semibold border-b-2 transition-colors flex items-center gap-2 whitespace-nowrap ${
                activeTab === 'fastest_growing'
                  ? 'border-blue-600 text-blue-600 dark:border-blue-400 dark:text-blue-400'
                  : 'border-transparent text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-slate-200'
              }`}
            >
              <TrendingUp className="w-4 h-4" />
              Fastest Growing
            </button>
          </div>
        </div>
      </div>

      {/* Main Content Area */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        {/* Filters Bar */}
        <div className="grid grid-cols-1 sm:grid-cols-12 gap-3 mb-6">
          {/* Search Input */}
          <div className="sm:col-span-6 relative">
            <Search className="w-4 h-4 absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400" />
            <input
              type="text"
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder="Filter by app name, developer, bundle ID..."
              className="w-full pl-9 pr-4 py-2 text-sm bg-white dark:bg-slate-900 border border-slate-300 dark:border-slate-800 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500/50 text-slate-900 dark:text-white"
            />
          </div>

          {/* Category Selector */}
          <div className="sm:col-span-3">
            <select
              value={selectedCategory}
              onChange={e => setSelectedCategory(e.target.value)}
              aria-label="Filter apps by category"
              className="w-full px-3 py-2 text-sm bg-white dark:bg-slate-900 border border-slate-300 dark:border-slate-800 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500/50 text-slate-900 dark:text-white"
            >
              <option value="all">All Categories (22)</option>
              {CATEGORIES_LIST.map(cat => (
                <option key={cat.id} value={cat.id}>
                  {cat.name}
                </option>
              ))}
            </select>
          </div>

          {/* Country Selector */}
          <div className="sm:col-span-3">
            <select
              value={selectedCountry}
              onChange={e => setSelectedCountry(e.target.value)}
              aria-label="Filter apps by storefront country"
              className="w-full px-3 py-2 text-sm bg-white dark:bg-slate-900 border border-slate-300 dark:border-slate-800 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500/50 text-slate-900 dark:text-white"
            >
              {STOREFRONT_COUNTRIES.map(c => (
                <option key={c.code} value={c.code}>
                  {c.flag} {c.name} ({c.code})
                </option>
              ))}
            </select>
          </div>
        </div>

        {/* Informational Guidance Alert */}
        <div className="mb-6 p-3.5 bg-blue-50/70 dark:bg-blue-950/30 border border-blue-200/80 dark:border-blue-900/50 rounded-xl flex items-start gap-3 text-xs text-blue-900 dark:text-blue-300">
          <ShieldCheck className="w-4 h-4 text-blue-600 dark:text-blue-400 shrink-0 mt-0.5" />
          <div>
            <span className="font-semibold">AppStatsView Ingestion Policy:</span>{' '}
            {activeTab === 'new_releases' && (
              <span>
                Apps in this tab have verified Apple Public Release Dates within the last 30 days. No synthetic or generated apps are ever indexed.
              </span>
            )}
            {activeTab === 'recently_discovered' && (
              <span>
                Apps in this tab were recently discovered by our multi-channel engine (Apple RSS Charts, vocabulary expansion, developer graphs). Their actual release date may be earlier, clearly distinguishing <em>Recently Indexed</em> from <em>New Releases</em>.
              </span>
            )}
            {activeTab === 'fastest_growing' && (
              <span>
                Recent and newly discovered applications displaying high search position velocity, rating momentum, and review volume.
              </span>
            )}
          </div>
        </div>

        {/* App Grid */}
        {isLoading ? (
          <div className="flex flex-col items-center justify-center py-20">
            <Loader2 className="w-8 h-8 text-blue-600 animate-spin mb-3" />
            <p className="text-sm text-slate-500 dark:text-slate-400">Querying verified App Store catalog...</p>
          </div>
        ) : appsList.length === 0 ? (
          <div className="text-center py-16 bg-white dark:bg-slate-900/40 rounded-2xl border border-slate-200 dark:border-slate-800 p-8">
            <Compass className="w-10 h-10 text-slate-400 mx-auto mb-3" />
            <h3 className="text-base font-semibold text-slate-900 dark:text-white">No applications match the filter</h3>
            <p className="text-sm text-slate-500 dark:text-slate-400 mt-1 max-w-sm mx-auto">
              Try adjusting your category or country storefront, or type a broader keyword search.
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
            {appsList.map(app => {
              const growth = getGrowthClassification(app.growthScore || 50);
              const isNew = app.isNewRelease || (app.firstReleaseDate && (Date.now() - new Date(app.firstReleaseDate).getTime()) / (24 * 3600 * 1000) <= 30);

              return (
                <div
                  key={app.id || app.appStoreId}
                  onClick={() => onSelectApp(String(app.appStoreId || app.id))}
                  className="group relative bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-xl p-4 hover:border-blue-500/50 dark:hover:border-blue-500/50 hover:shadow-lg transition-all duration-200 cursor-pointer flex flex-col justify-between"
                >
                  <div>
                    {/* Top Row: App Icon & Header Badges */}
                    <div className="flex items-start gap-3 mb-3">
                      <AppIcon
                        src={app.iconUrl}
                        alt={app.name}
                        size={56}
                        className="rounded-xl shadow-sm border border-slate-100 dark:border-slate-800 shrink-0"
                      />
                      <div className="min-w-0 flex-1">
                        <h3 className="font-semibold text-sm text-slate-900 dark:text-white truncate group-hover:text-blue-600 dark:group-hover:text-blue-400 transition-colors">
                          {app.name}
                        </h3>
                        <p className="text-xs text-slate-500 dark:text-slate-400 truncate mt-0.5">
                          {app.developer}
                        </p>
                        <div className="flex items-center gap-1.5 mt-1.5">
                          <span className="text-[11px] font-medium px-2 py-0.5 rounded bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-300">
                            {app.category}
                          </span>
                          <span className="text-[11px] text-slate-500 dark:text-slate-400">
                            {app.price || 'Free'}
                          </span>
                        </div>
                      </div>
                    </div>

                    {/* Discovery / Release Badge */}
                    <div className="mb-3">
                      {isNew ? (
                        <div className="inline-flex items-center gap-1 text-[11px] font-semibold px-2 py-0.5 rounded bg-emerald-50 text-emerald-700 dark:bg-emerald-950/40 dark:text-emerald-400 border border-emerald-200/50 dark:border-emerald-800/40">
                          <Sparkles className="w-3 h-3 text-emerald-600 dark:text-emerald-400" />
                          <span>{formatReleaseDate(app.firstReleaseDate)}</span>
                        </div>
                      ) : (
                        <div className="inline-flex items-center gap-1 text-[11px] font-medium px-2 py-0.5 rounded bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-400">
                          <Clock className="w-3 h-3" />
                          <span>{formatDiscoveredDate(app.discoveredAt || app.lastSyncedAt)}</span>
                        </div>
                      )}
                    </div>

                    {/* Metadata Strip */}
                    <div className="grid grid-cols-2 gap-2 py-2 border-t border-slate-100 dark:border-slate-800/80 text-xs">
                      <div>
                        <span className="text-slate-500 dark:text-slate-400 block text-[10px]">Rating</span>
                        <span className="font-semibold text-slate-900 dark:text-white">
                          ★ {(app.rating || 4.5).toFixed(1)}{' '}
                          <span className="text-slate-400 text-[10px] font-normal">
                            ({(app.totalReviews || app.ratingCount || 0).toLocaleString()})
                          </span>
                        </span>
                      </div>
                      <div>
                        <span className="text-slate-500 dark:text-slate-400 block text-[10px]">Rank</span>
                        <span className="font-semibold text-slate-900 dark:text-white">
                          #{app.currentRank || app.searchPosition || 25}
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* Footer Bar */}
                  <div className="mt-3 pt-2.5 border-t border-slate-100 dark:border-slate-800 flex items-center justify-between text-xs">
                    <div className="flex items-center gap-1 text-slate-500 dark:text-slate-400">
                      <Globe className="w-3.5 h-3.5" />
                      <span>{app.country || selectedCountry} Store</span>
                    </div>
                    <span className="text-blue-600 dark:text-blue-400 font-medium group-hover:underline">
                      View Profile →
                    </span>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* Load More Pagination */}
        {hasMore && !isLoading && (
          <div className="mt-8 text-center">
            <button
              onClick={() => fetchApps(currentPage + 1, false)}
              disabled={isLoadingMore}
              className="inline-flex items-center gap-2 px-6 py-2.5 text-sm font-semibold text-white bg-blue-600 hover:bg-blue-700 rounded-xl shadow-sm transition-colors disabled:opacity-50"
            >
              {isLoadingMore ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Loading More Applications...
                </>
              ) : (
                'Load More Discovered Apps'
              )}
            </button>
          </div>
        )}
      </div>
    </div>
  );
};
